import { useState } from 'react'
import {
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material'
import { BugReport, ReportProblem, Speed } from '@mui/icons-material'

import { useGetErrorsQuery, useGetPerformanceQuery } from '@/apis/v2Api'
import type { VitalRating } from '@/apis/v2Mappers'
import { useReportArgs } from '@/hooks/useReportArgs'
import type { Period } from '@/utils/dates'

import { ErrorGroupsTab } from './ErrorGroupsTab'
import { ReportHeader, ReportStatus, SectionCard, fmtDateTime, fmtNum } from './common'

const RATING: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
    good: { label: 'Good', color: 'success' },
    needs_improvement: { label: 'Needs work', color: 'warning' },
    poor: { label: 'Poor', color: 'error' },
}

export function RatingChip({ rating }: { rating: VitalRating }) {
    if (!rating) {
        return null
    }
    const r = RATING[rating]
    return <Chip size="small" label={r.label} color={r.color} variant="outlined" />
}

export const fmtVital = (metric: string, v: number) =>
    metric === 'CLS' ? v.toFixed(3) : `${fmtNum(v)} ms`

function Performance({ period }: { period: Period }) {
    const args = useReportArgs(period)
    const { data, isLoading, error } = useGetPerformanceQuery(args)
    return (
        <ReportStatus
            isLoading={isLoading}
            error={error}
            isEmpty={!!data && data.metrics.length === 0}
            emptyIcon={<Speed />}
            emptyTitle="No Web Vitals in this range"
            emptyDescription="The browser SDK sends web_vital events (LCP, INP, CLS, FCP, TTFB) when enabled."
        >
            <Grid container spacing={2}>
                {(data?.metrics ?? []).map(m => (
                    <Grid key={m.metric} size={{ xs: 12, md: 6 }}>
                        <SectionCard
                            title={`${m.metric} · p75 ${fmtVital(m.metric, m.p75)}`}
                            action={<RatingChip rating={m.rating} />}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Device</TableCell>
                                        <TableCell align="right">Samples</TableCell>
                                        <TableCell align="right">p50</TableCell>
                                        <TableCell align="right">p75</TableCell>
                                        <TableCell align="right">p95</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {m.by_device.map(d => (
                                        <TableRow key={d.device}>
                                            <TableCell>{d.device || '(unknown)'}</TableCell>
                                            <TableCell align="right">{fmtNum(d.samples)}</TableCell>
                                            <TableCell align="right">
                                                {fmtVital(m.metric, d.p50)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtVital(m.metric, d.p75)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtVital(m.metric, d.p95)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <RatingChip rating={d.rating} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </SectionCard>
                    </Grid>
                ))}
            </Grid>
        </ReportStatus>
    )
}

function Errors({ period }: { period: Period }) {
    const args = useReportArgs(period)
    const { data, isLoading, error } = useGetErrorsQuery({ ...args, limit: 200 })
    return (
        <ReportStatus
            isLoading={isLoading}
            error={error}
            isEmpty={!!data && data.errors.length === 0}
            emptyIcon={<BugReport />}
            emptyTitle="No errors in this range"
        >
            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Fingerprint</TableCell>
                                <TableCell align="right">Count</TableCell>
                                <TableCell align="right">Sessions</TableCell>
                                <TableCell>First seen</TableCell>
                                <TableCell>Last seen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data?.errors ?? []).map(e => (
                                <TableRow key={`${e.error_type}-${e.fingerprint}`} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {e.error_type}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 280 }}>
                                        <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{ fontFamily: 'monospace', display: 'block' }}
                                            title={e.fingerprint}
                                        >
                                            {e.fingerprint || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{fmtNum(e.count)}</TableCell>
                                    <TableCell align="right">{fmtNum(e.sessions)}</TableCell>
                                    <TableCell>{fmtDateTime(e.first_seen)}</TableCell>
                                    <TableCell>{fmtDateTime(e.last_seen)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </ReportStatus>
    )
}

/** v2 error groups (triage), `/reports/errors` and `/reports/performance` (Core Web Vitals). */
export function QualityPage() {
    const [period, setPeriod] = useState<Period>('7d')
    const [tab, setTab] = useState(0)
    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Errors & Performance"
                period={tab === 0 ? undefined : period}
                onPeriodChange={tab === 0 ? undefined : setPeriod}
            />
            <Tabs
                value={tab}
                onChange={(_, v: number) => setTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
                <Tab icon={<ReportProblem />} iconPosition="start" label="Issues" />
                <Tab icon={<BugReport />} iconPosition="start" label="Error events" />
                <Tab icon={<Speed />} iconPosition="start" label="Web Vitals" />
            </Tabs>
            {tab === 0 && <ErrorGroupsTab />}
            {tab === 1 && <Errors period={period} />}
            {tab === 2 && <Performance period={period} />}
        </Box>
    )
}
